import React, { useEffect, useState } from 'react';
import { getEvaluationHistory } from '@/services/evaluationService';
import { Loader2, Calendar, MapPin, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Pagination, PaginationContent, PaginationItem, PaginationLink } from "../ui/pagination";
import { ScrollArea } from "../ui/scroll-area";
import { useTranslation } from 'react-i18next';

interface EvaluationHistoryProps {
  profileId: string;
}

const ITEMS_PER_PAGE = 5;

const EvaluationHistory: React.FC<EvaluationHistoryProps> = ({ profileId }) => {
  const { t } = useTranslation();
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [expandedView, setExpandedView] = useState<boolean>(false);

  useEffect(() => {
    const fetchEvaluations = async () => {
      if (!profileId) return;
      
      try {
        setLoading(true);
        console.log('Intentando cargar historial para el perfil:', profileId);
        
        // Validación actualizada: permitimos el ID "1" para el perfil por defecto
        // así como cualquier UUID válido para perfiles reales
        const isValidUUID = profileId === '1' || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(profileId);
        
        if (!isValidUUID) {
          console.error('ID de perfil no válido:', profileId);
          toast.error(t('profile.invalidProfileId'));
          setLoading(false);
          return;
        }
        
        // Si es el perfil por defecto, no mostramos evaluaciones
        if (profileId === '1') {
          setEvaluations([]);
          setLoading(false);
          return;
        }
        
        const data = await getEvaluationHistory(profileId);
        console.log('Evaluaciones recibidas:', data);
        setEvaluations(data);
        setTotalPages(Math.ceil(data.length / ITEMS_PER_PAGE));
        setHasMore(data.length > ITEMS_PER_PAGE);
      } catch (error: any) {
        console.error('Error fetching evaluations:', error);
        toast.error(t('profile.evaluationHistory.loadError', { error: error.message }));
      } finally {
        setLoading(false);
      }
    };
    
    // Reiniciar las evaluaciones y la página actual cuando cambia el perfil
    setEvaluations([]);
    setCurrentPage(1);
    fetchEvaluations();
  }, [profileId]); // Añadir profileId como dependencia para que se actualice cuando cambie

  // Función para mostrar más resultados
  const loadMore = () => {
    setExpandedView(true);
  };

  // Cálculo de evaluaciones para la página actual
  const getPaginatedEvaluations = () => {
    if (expandedView) {
      return evaluations;
    }
    
    const start = 0;
    const end = ITEMS_PER_PAGE;
    return evaluations.slice(start, end);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="w-6 h-6 text-sangre-500 animate-spin" />
        <span className="ml-2">{t('profile.evaluationHistory.loading')}</span>
      </div>
    );
  }

  if (evaluations.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        {t('profile.evaluationHistory.noEvaluations')}
      </div>
    );
  }

  const getScoreColor = (score: number) => {
    if (score > 85) return 'text-fuerte-500';
    if (score > 60) return 'text-warning-500';
    return 'text-sangre-500';
  };

  const paginatedEvaluations = getPaginatedEvaluations();

  return (
    <div className="space-y-4 mt-4">
      <h3 className="text-base font-semibold">{t('profile.evaluationHistory.title')}</h3>
      
      <ScrollArea className="max-h-[600px]">
        <div className="space-y-4">
          {paginatedEvaluations.map((evaluation) => (
            <div key={evaluation.id} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
              <div className="flex justify-between items-center mb-2">
                <span className={`font-bold text-lg ${getScoreColor(evaluation.score)}`}>
                  {evaluation.score}/100
                </span>
                <span className="text-xs text-gray-500 flex items-center">
                  <Calendar className="w-3 h-3 mr-1" />
                  {format(new Date(evaluation.created_at), "dd MMM yyyy, HH:mm", { locale: es })}
                </span>
              </div>
              
              <p className="text-sm text-gray-700 mb-1">{evaluation.result_category}</p>
              <p className="text-xs text-gray-600">{evaluation.interpretation}</p>
              
              {(evaluation.latitude || evaluation.longitude) && (
                <div className="mt-2 flex items-center text-xs text-gray-500">
                  <MapPin className="w-3 h-3 mr-1" />
                  {t('profile.evaluationHistory.location.latitude')}: {evaluation.latitude ? evaluation.latitude.toFixed(6) : t('profile.evaluationHistory.location.notAvailable')}, 
                  {t('profile.evaluationHistory.location.longitude')}: {evaluation.longitude ? evaluation.longitude.toFixed(6) : t('profile.evaluationHistory.location.notAvailable')}
                </div>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>
      
      {!expandedView && hasMore && (
        <div className="flex justify-center mt-4">
          <button 
            onClick={loadMore}
            className="flex items-center text-sangre-600 hover:text-sangre-700 text-sm font-medium"
          >
            {t('profile.evaluationHistory.viewAll')}
            <ChevronDown className="ml-1 w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default EvaluationHistory;
